"use client";

import { useSearchParams } from "next/navigation";
import React, { type ComponentProps, Suspense, useMemo } from "react";
import { Link, usePathname } from "./AppRouterLibs";

interface SearchLinkProps extends Omit<ComponentProps<typeof Link>, "href"> {
  /**
   * The href to use for the link.
   * If not provided, the link will use the current pathname.
   */
  href?: string;
  /**
   * The search parameter key to use
   */
  field: string;
  /**
   * The search parameter value to use
   */
  value: string;
  /**
   * Should pressing the link toggle the value?
   */
  toggle?: boolean;
  /**
   * Should the link append the value to the existing search params when there is already a key with the same value?
   */
  append?: boolean;
}

const UnsuspendedSearchLink = ({
  href: incomingHref,
  field: key,
  value,
  toggle = true,
  append,
  shallow,
  ...props
}: SearchLinkProps) => {
  const readonlySearchParams = useSearchParams();
  const paramString = readonlySearchParams.toString();
  const pathname = usePathname();

  let href = pathname;
  if (incomingHref) {
    href = incomingHref;
  }

  const realHref = useMemo(() => {
    const params = new URLSearchParams(paramString);
    const add = append ? params.append.bind(params) : params.set.bind(params);
    let hrefToUse: string = href;
    if (toggle) {
      if (params.has(key, value)) {
        params.delete(key, value);
      } else {
        add(key, value);
      }
    } else {
      if (!params.has(key, value)) {
        add(key, value);
      }
    }

    if (!hrefToUse.includes("?")) {
      hrefToUse = `${href}?${params.toString()}`;
    } else {
      // Parse params from href and merge with searchParams
      const hrefParams = new URLSearchParams(hrefToUse.split("?")[1]);
      for (const [key, value] of hrefParams) {
        if (params.has(key, value)) {
          params.delete(key, value);
        }
        params.append(key, value);
      }

      hrefToUse = `${hrefToUse.split("?")[0]}?${params.toString()}`;
    }

    return hrefToUse;
  }, [paramString, toggle, key, value, append, href]);

  const active = readonlySearchParams.has(key, value);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (shallow) {
      if (e.ctrlKey || e.metaKey) {
        return;
      }
      e.preventDefault();

      const navigate = props.replace
        ? window.history.replaceState
        : window.history.pushState;

      navigate(null, "", realHref);
    }
  };

  return (
    <Link
      onClick={handleClick}
      shallow
      href={realHref}
      data-active={active}
      {...props}
    />
  );
};

export const SearchParamLink: typeof UnsuspendedSearchLink = ({ ...props }) => {
  return (
    <Suspense
      fallback={<Link {...props} href={props.href ? props.href : ""} />}
    >
      <UnsuspendedSearchLink {...props} />
    </Suspense>
  );
};
